/**
 * Phase 27b — PTO balance panel for the profile page.
 *
 * - Shows one row per PTO type from GET /api/tenant/pto-balances
 *   (dual-use endpoint: worker → own, admin → passes identity_email).
 * - Below the balance grid, a Collapsible "Ledger" section lists up to
 *   the last 20 entries from GET /api/tenant/pto-ledger.
 * - Hidden via `runtime_engines.pto=false` handling upstream — the
 *   parent page decides whether to render this component at all.
 *
 * Props:
 *   identityEmail?  — admin viewing another worker's profile.
 *                     Omit for "self" view.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from "@/components/ui/card";
import {
  Collapsible, CollapsibleContent, CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  PalmtreeIcon, ChevronDown, ChevronUp, ScrollText, Loader2, TrendingDown,
} from "lucide-react";

const REASON_LABELS = {
  approve: "Approved & deducted",
  cancel: "Cancelled — credited back",
  reject_release: "Rejected — reserve released",
  manual_grant: "Manual grant",
  import_correction: "Import correction",
};

function fmtDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString(undefined, {
    month: "short", day: "numeric", year: "numeric",
  });
}

/**
 * Phase 29 · Item 4 — 90-day burn-rate summary for one PTO type.
 * Sums approved deductions (reason === "approve", delta_hours < 0)
 * from the ledger for the last 90 days. Returns:
 *   { hoursBurned, monthlyRate, projection }
 * where `projection` is a runout date string (or "" if not applicable).
 */
function computeBurn(ledger, ptoTypeCode, availableHours) {
  const cutoff = Date.now() - 90 * 24 * 60 * 60 * 1000;
  let hoursBurned = 0;
  for (const l of ledger) {
    if (l.pto_type_code !== ptoTypeCode) continue;
    if (l.reason !== "approve") continue;
    const t = new Date(l.created_at).getTime();
    if (!Number.isFinite(t) || t < cutoff) continue;
    const d = Number(l.delta_hours || 0);
    if (d < 0) hoursBurned += Math.abs(d);
  }
  // Approved deductions in the last 90 days → monthly rate.
  const monthlyRate = hoursBurned > 0 ? (hoursBurned / 90) * 30 : 0;
  let projection = "";
  if (monthlyRate > 0 && availableHours > 0) {
    const monthsLeft = availableHours / monthlyRate;
    const d = new Date();
    d.setMonth(d.getMonth() + Math.round(monthsLeft));
    projection = d.toLocaleDateString(undefined, {
      month: "short", year: "numeric",
    });
  }
  return { hoursBurned, monthlyRate, projection };
}

export default function PtoBalancePanel({ identityEmail = null }) {
  const [balances, setBalances] = useState([]);
  const [ledger, setLedger] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [ledgerOpen, setLedgerOpen] = useState(false);
  const [err, setErr] = useState("");
  // Phase 29 · Item 4 — 90-day burn-rate ledger (fetched once for the panel).
  // We pull up to 200 rows and derive per-type burn client-side so we don't
  // fan out N calls when a workspace has many PTO types.
  const [burnLedger, setBurnLedger] = useState([]);

  useEffect(() => {
    let cancelled = false;
    const params = identityEmail ? { identity_email: identityEmail } : {};
    setLoading(true);
    setErr("");
    api
      .get("/tenant/pto-balances", { params })
      .then((r) => {
        if (cancelled) return;
        setBalances(r.data?.items || []);
      })
      .catch((e) => {
        if (cancelled) return;
        setErr(
          e?.response?.data?.detail ||
            e?.message ||
            "Could not load PTO balances"
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    // Fire the burn-rate ledger fetch alongside the balances fetch.
    const bParams = identityEmail
      ? { identity_email: identityEmail, limit: 200 }
      : { limit: 200 };
    api
      .get("/tenant/pto-ledger", { params: bParams })
      .then((r) => { if (!cancelled) setBurnLedger(r.data?.items || []); })
      .catch(() => { if (!cancelled) setBurnLedger([]); });
    return () => { cancelled = true; };
  }, [identityEmail]);

  useEffect(() => {
    if (!ledgerOpen || ledger.length > 0) return;
    let cancelled = false;
    const params = identityEmail
      ? { identity_email: identityEmail, limit: 20 }
      : { limit: 20 };
    setLedgerLoading(true);
    api
      .get("/tenant/pto-ledger", { params })
      .then((r) => { if (!cancelled) setLedger(r.data?.items || []); })
      .catch(() => { if (!cancelled) setLedger([]); })
      .finally(() => { if (!cancelled) setLedgerLoading(false); });
    return () => { cancelled = true; };
  }, [ledgerOpen, identityEmail, ledger.length]);

  return (
    <Card data-testid="pto-balance-panel">
      <CardHeader className="flex flex-row items-start gap-3 space-y-0">
        <div className="rounded-xl bg-secondary/15 p-3 text-secondary">
          <PalmtreeIcon className="h-5 w-5" />
        </div>
        <div>
          <CardTitle>Paid time off</CardTitle>
          <CardDescription>
            Snapshot balances after approved / pending reservations.
          </CardDescription>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading balances…
          </div>
        ) : err ? (
          <div className="text-sm text-destructive" data-testid="pto-balance-error">
            {String(err)}
          </div>
        ) : balances.length === 0 ? (
          <div className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">
            No PTO types configured yet.
          </div>
        ) : (
          <div className="grid gap-2 sm:grid-cols-2">
            {balances.map((row) => {
              const pending = Number(row.pending_hours || 0);
              const balance = Number(row.balance_hours || 0);
              const available = Number(row.available_hours ?? balance - pending);
              const burn = computeBurn(burnLedger, row.pto_type_code, available);
              return (
                <div
                  key={row.id}
                  className="rounded-lg border p-3"
                  data-testid={`pto-balance-row-${row.pto_type_code || row.pto_type_id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="inline-block h-2.5 w-2.5 rounded-full bg-secondary" />
                      <div className="font-medium">{row.pto_type_label}</div>
                    </div>
                    <Badge variant="outline" className="rounded-full text-[10px] uppercase">
                      {row.pto_type_code}
                    </Badge>
                  </div>
                  <div className="mt-2 grid grid-cols-3 gap-2 text-center">
                    <Stat label="Available" value={available.toFixed(2)} tone="primary" />
                    <Stat label="Pending" value={pending.toFixed(2)} tone="amber" />
                    <Stat label="Balance" value={balance.toFixed(2)} tone="muted" />
                  </div>
                  <div
                    className="mt-2 flex items-center gap-2 border-t border-dashed pt-2 text-[11px]"
                    data-testid={`pto-burn-${row.pto_type_code || row.pto_type_id}`}
                  >
                    <TrendingDown className="h-3 w-3 text-muted-foreground" />
                    {burn.monthlyRate > 0 ? (
                      <span className="text-muted-foreground">
                        ~<span className="font-medium text-primary tabular-nums">
                          {burn.monthlyRate.toFixed(1)}h
                        </span>/mo burn rate
                        {burn.projection && (
                          <>
                            {" · "}
                            <span>at this pace you'll hit 0 by </span>
                            <span className="font-medium text-primary">{burn.projection}</span>
                          </>
                        )}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">
                        No recent usage in the last 90 days.
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <Collapsible open={ledgerOpen} onOpenChange={setLedgerOpen}>
          <CollapsibleTrigger asChild>
            <Button
              variant="ghost"
              className="w-full justify-between"
              data-testid="pto-ledger-toggle"
            >
              <span className="flex items-center gap-2 text-sm font-medium">
                <ScrollText className="h-4 w-4" />
                Ledger (last 20)
              </span>
              {ledgerOpen ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent className="pt-2">
            {ledgerLoading ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading ledger…
              </div>
            ) : ledger.length === 0 ? (
              <div className="rounded-lg border border-dashed p-4 text-center text-xs text-muted-foreground">
                No ledger entries yet.
              </div>
            ) : (
              <div className="overflow-hidden rounded-lg border">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50 text-xs uppercase tracking-widest text-muted-foreground">
                    <tr>
                      <th className="px-3 py-2 text-left">Date</th>
                      <th className="px-3 py-2 text-left">Type</th>
                      <th className="px-3 py-2 text-right">Δ hrs</th>
                      <th className="px-3 py-2 text-right">Balance after</th>
                      <th className="px-3 py-2 text-left">Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ledger.map((l) => (
                      <tr
                        key={l.id}
                        className="border-t"
                        data-testid={`pto-ledger-row-${l.id}`}
                      >
                        <td className="px-3 py-2 text-muted-foreground">
                          {fmtDate(l.created_at)}
                        </td>
                        <td className="px-3 py-2">
                          <Badge variant="outline" className="rounded-full text-[10px]">
                            {l.pto_type_code}
                          </Badge>
                        </td>
                        <td
                          className={`px-3 py-2 text-right font-mono ${
                            Number(l.delta_hours) < 0
                              ? "text-destructive"
                              : Number(l.delta_hours) > 0
                              ? "text-success"
                              : "text-muted-foreground"
                          }`}
                        >
                          {Number(l.delta_hours) > 0 ? "+" : ""}
                          {Number(l.delta_hours).toFixed(2)}
                        </td>
                        <td className="px-3 py-2 text-right font-mono">
                          {Number(l.balance_hours_after || 0).toFixed(2)}
                        </td>
                        <td className="px-3 py-2 text-xs">
                          {REASON_LABELS[l.reason] || l.reason}
                          {l.note && (
                            <span className="ml-1 italic text-muted-foreground">
                              — {l.note}
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CollapsibleContent>
        </Collapsible>
      </CardContent>
    </Card>
  );
}

function Stat({ label, value, tone }) {
  const cls = {
    primary: "text-primary",
    amber: "text-amber-600",
    muted: "text-muted-foreground",
  }[tone] || "text-primary";
  return (
    <div className="rounded-md bg-muted/40 p-2">
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
        {label}
      </div>
      <div className={`mt-0.5 font-display text-base tabular-nums ${cls}`}>
        {value}
      </div>
    </div>
  );
}
